import {
  BulletList,
  Container,
  JoinTeamSection,
  ProLandingSectionContent,
  SectionSubtitle,
  SocialsSection,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { missionCardImages } from './assets/mission';
import { solutionCardImages } from './assets/solution';
import storyImage from './assets/storyDog.webp';
import { topDecoration, topSections, topSeparator } from './assets/top';
import { separatorBlueBottom, separatorBlueTop, valuesCardImages } from './assets/values';
import * as Styled from './styled';
import type { StoryPageProps } from './types';

export const StoryPage: FC<StoryPageProps> = (props) => {
  const { translate: translateRessource } = useTranslation({ keyPrefix: 'story' });
  const { joinLink } = props;

  const missionCardItems = useMemo(() => {
    const items = Object.values(
      translateRessource('mission.cards.items', { returnObjects: true }) ?? {},
    );
    return items.map((item: any) => ({
      title: item.title,
      texts: Object.values(item.text.items) as string[],
    }));
  }, [translateRessource]);

  const valuesCardItems = useMemo(() => {
    const items = Object.values(
      translateRessource('values.cards.items', { returnObjects: true }) ?? {},
    );
    return items.map((item: any) => ({
      title: item.title,
      text: item.text,
    }));
  }, [translateRessource]);

  const storySectionItems = useMemo(() => {
    const items = Object.values(
      translateRessource('story.sections.items', { returnObjects: true }) ?? {},
    );
    return items.map((item: any) => ({
      subtitle: item.title as string,
      text: item.text as string,
    }));
  }, [translateRessource]);

  const solutionCardItems = useMemo(() => {
    const items = Object.values(
      translateRessource('solution.cards.items', { returnObjects: true }) ?? {},
    );
    return items.map((item: any) => ({
      title: item.title,
      text: item.text,
    }));
  }, [translateRessource]);

  return (
    <Styled.PageWrapper>
      <Styled.TopWrapper>
        <Styled.TopDecoration src={topDecoration} alt="top decoration" />
        <Styled.TopSeparator src={topSeparator} alt="top separator" />
        <Styled.TopContainer>
          <Styled.TopTitle>{translateRessource('top.title')}</Styled.TopTitle>
          <Styled.TopImagesContainer>
            {topSections.map((images, index) => (
              <Styled.TopImagesContainerSection key={index}>
                {images.map((image, imageIndex) => (
                  <Styled.TopImageWrapper key={imageIndex}>
                    <Styled.TopImage src={image} alt="top image" />
                  </Styled.TopImageWrapper>
                ))}
              </Styled.TopImagesContainerSection>
            ))}
          </Styled.TopImagesContainer>
        </Styled.TopContainer>
      </Styled.TopWrapper>
      <Styled.MissionWrapper>
        <Container>
          <Styled.MissionTitle color="purple">
            {translateRessource('mission.title')}
          </Styled.MissionTitle>
          <SectionSubtitle>{translateRessource('mission.subtitle')}</SectionSubtitle>
          <Styled.MissionCardContainer>
            {missionCardItems.map((item, index) => (
              <Styled.MissionCard key={index}>
                <Styled.MissionCardImage src={missionCardImages[index]} alt="mission image" />
                <Styled.MissionCardTitle>{item.title}</Styled.MissionCardTitle>
                <BulletList items={item.texts} />
              </Styled.MissionCard>
            ))}
          </Styled.MissionCardContainer>
        </Container>
      </Styled.MissionWrapper>

      <Styled.ValuesWrapper>
        <Styled.ValuesTitle>{translateRessource('values.title')}</Styled.ValuesTitle>
        <Styled.ValuesSeparatorTop src={separatorBlueTop} />
        <Container>
          <Styled.ValuesCardContainer>
            {valuesCardItems.map((item, index) => (
              <Styled.ValuesCardWrapper key={index}>
                <Styled.ValuesCard>
                  <Styled.ValuesCardImage src={valuesCardImages[index]} alt="values image" />
                  <Styled.ValuesCardTitle>{item.title}</Styled.ValuesCardTitle>
                  <p>{item.text}</p>
                </Styled.ValuesCard>
              </Styled.ValuesCardWrapper>
            ))}
          </Styled.ValuesCardContainer>
        </Container>
        <Styled.ValuesSeparatorBottom src={separatorBlueBottom} />
      </Styled.ValuesWrapper>
      <ProLandingSectionContent
        image={storyImage}
        title={translateRessource('story.title')}
        subtitle={translateRessource('story.subtitle')}
        contents={storySectionItems}
      />

      <Container>
        <Styled.SolutionWrapper>
          <Styled.SolutionTitle color="purple">
            {translateRessource('solution.title')}
          </Styled.SolutionTitle>
          <Styled.SolutionText>{translateRessource('solution.subtitle')}</Styled.SolutionText>
          <Styled.SolutionCardContainer>
            {solutionCardItems.map((item, index) => (
              <Styled.SolutionCardWrapper key={index}>
                <Styled.SolutionCard>
                  <Styled.SolutionCardImage src={solutionCardImages[index]} alt="solution image" />
                  <Styled.SolutionCardTitle>{item.title}</Styled.SolutionCardTitle>
                  <p>{item.text}</p>
                </Styled.SolutionCard>
              </Styled.SolutionCardWrapper>
            ))}
          </Styled.SolutionCardContainer>
        </Styled.SolutionWrapper>

        <JoinTeamSection link={joinLink} />

        <Styled.SocialsWrapper>
          <SocialsSection />
        </Styled.SocialsWrapper>
      </Container>
    </Styled.PageWrapper>
  );
};
